// Video metadata extraction utility

interface VideoMetadata {
  title?: string;
  description?: string;
  thumbnail?: string;
  duration?: number;
  source?: string;
  embedUrl?: string;
  error?: string;
}

/**
 * Extract video metadata from popular video platforms and provide embed URLs.
 * Currently supports YouTube and Vimeo.
 */
export async function extractVideoMetadata(url: string): Promise<VideoMetadata> {
  try {
    const parsed = new URL(url);
    
    // YouTube
    if (parsed.hostname.includes('youtube.com') || parsed.hostname.includes('youtu.be')) {
      const videoId = getYouTubeVideoId(url);
      if (videoId) {
        return {
          title: `YouTube Video: ${videoId}`,
          thumbnail: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
          source: 'youtube',
          embedUrl: `https://www.youtube.com/embed/${videoId}`,
        };
      }
    }

    // Vimeo
    if (parsed.hostname.includes('vimeo.com')) {
      const vimeoId = parsed.pathname.split('/')[2];
      if (vimeoId) {
        return {
          title: `Vimeo Video: ${vimeoId}`,
          source: 'vimeo',
          embedUrl: `https://player.vimeo.com/video/${vimeoId}`,
        };
      }
    }

    // Default: treat the URL as a direct video source
    return { embedUrl: url };
  } catch {
    return { error: 'Invalid URL' };
  }
}

export function getYouTubeVideoId(url: string): string | null {
  try {
    const parsed = new URL(url);
    
    // youtu.be short URL
    if (parsed.hostname === 'youtu.be') {
      return parsed.pathname.slice(1);
    }
    
    // youtube.com watch URL
    if (parsed.hostname.includes('youtube.com')) {
      return parsed.searchParams.get('v');
    }
    
    return null;
  } catch {
    return null;
  }
}

/**
 * Get the best available thumbnail URL from video metadata.
 * Works with YouTube thumbnails from our metadata extraction.
 * Falls back to the embed URL source URL if no thumbnail is available.
 */
export function getThumbnailUrl(videoUrl: string, source?: string): string | undefined {
  // YouTube thumbnail extraction
  const videoId = getYouTubeVideoId(videoUrl);
  if (videoId) {
    // Return high quality default thumbnail
    return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  }

  // Vimeo: extract video ID and return a placeholder (Vimeo generates on-the-fly)
  if (source === 'vimeo' || videoUrl.includes('vimeo.com')) {
    try {
      const parsed = new URL(videoUrl);
      const vimeoId = parsed.pathname.split('/')[2];
      if (vimeoId) {
        // Use vimeo's thumbnail API endpoint (returns JSON with thumbnail_url)
        return undefined; // We'll let the UI fallback handle this gracefully
      }
    } catch {
      // fall through
    }
  }

  // For direct video URLs, we can't generate a thumbnail automatically
  // The UI will display a placeholder instead
  return undefined;
}

/**
 * Normalize a video URL to its canonical watch/embed-derivable format.
 * For YouTube, this means converting to the standard watch?v= format.
 * This ensures events always store the canonical URL, not embed URLs.
 */
export function normalizeVideoUrl(url: string): string {
  try {
    const parsed = new URL(url);
    
    // YouTube - normalize to watch?v= format
    if (parsed.hostname.includes('youtube.com') || parsed.hostname.includes('youtu.be')) {
      const videoId = getYouTubeVideoId(url);
      if (videoId) {
        return `https://www.youtube.com/watch?v=${videoId}`;
      }
    }
    
    // Vimeo - normalize to standard watch format
    if (parsed.hostname.includes('vimeo.com')) {
      const vimeoId = parsed.pathname.split('/')[2];
      if (vimeoId) {
        return `https://vimeo.com/${vimeoId}`;
      }
    }
    
    // Return original URL if no normalization needed
    return url;
  } catch {
    return url;
  }
}

/**
 * Extract a hosted image URL from an imeta tag string.
 * Handles the format: "url https://... image https://... m video/mp4"
 */
export function extractThumbnailFromImeta(imeta?: string): string | undefined {
  if (!imeta) return undefined;
  
  // Try to find the image field in imeta
  const imageMatch = imeta.match(/image\s+(https?:\/\/[^\s]+)/);
  if (imageMatch) {
    return imageMatch[1];
  }
  
  return undefined;
}

/**
 * Get the embed URL for iframe display from any video URL format.
 * Handles YouTube watch URLs, embed URLs, and other platforms.
 * Returns the embed URL, or the original URL if not a recognized platform.
 */
export function getVideoEmbedUrl(videoUrl: string): string | null {
  try {
    const parsed = new URL(videoUrl);
    
    // YouTube - convert any format to embed format
    if (parsed.hostname.includes('youtube.com') || parsed.hostname.includes('youtu.be')) {
      const videoId = getYouTubeVideoId(videoUrl);
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }
    
    // Vimeo
    if (parsed.hostname.includes('vimeo.com')) {
      const vimeoId = parsed.pathname.split('/')[2];
      if (vimeoId) {
        return `https://player.vimeo.com/video/${vimeoId}`;
      }
    }
    
    // For other URLs, return as-is (may be direct video links)
    return videoUrl;
  } catch {
    return null;
  }
}